import React, { forwardRef, PropsWithChildren, useMemo } from 'react';
import {
  Dialog,
  DialogProps,
  Slide,
  SlideProps,
  SxProps,
  Theme,
  TypographyProps,
} from '@mui/material';
import { ModalHeader } from 'components/ModalHeader/ModalHeader';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { useLayoutElements } from 'contexts/LayoutElementsContext/useLayoutElements';

export interface ModalProps extends DialogProps {
  open: boolean;
  onClose: () => void;
  onClickBack?: () => void;
  showHeader?: boolean;
  modalTitle: string;
  modalStyles?: SxProps<Theme>;
  overrideBackButtonVisible?: boolean;
  overrideCloseButtonVisible?: boolean;
  headerStyles?: SxProps<Theme>;
  modalTitleId: string;
  modalTitleProps?: TypographyProps;
  /** Show the favorite star in the header. Optional. */
  showFavorite?: boolean;
  /** Current favorite state. */
  isFavorited?: boolean;
  /** Called with the next favorite state when the star is tapped. */
  onToggleFavorite?: (nextIsFavorited: boolean) => void;
}

export const Modal = forwardRef<HTMLDivElement, PropsWithChildren<ModalProps>>(
  (
    {
      open,
      onClose,
      onClickBack,
      children,
      showHeader,
      modalTitle,
      overrideBackButtonVisible,
      overrideCloseButtonVisible,
      modalStyles,
      headerStyles,
      container,
      modalTitleId,
      modalTitleProps,
      fullScreen,
      showFavorite,
      isFavorited,
      onToggleFavorite,
      ...restProps
    },
    ref,
  ) => {
    const { isDesktop } = useDeviceType();
    const { isAlertOpen } = useLayoutElements();

    const showBackButton = useMemo(
      () => overrideBackButtonVisible ?? !isDesktop,
      [overrideBackButtonVisible, isDesktop],
    );

    const showCloseButton = useMemo(
      () => overrideCloseButtonVisible ?? isDesktop,
      [overrideCloseButtonVisible, isDesktop],
    );

    const DirectionalSlide = useMemo(
      () =>
        forwardRef((props: SlideProps, modalRef: React.Ref<unknown>) => (
          <Slide direction={(fullScreen ?? !isDesktop) ? 'left' : 'up'} ref={modalRef} {...props} />
        )),
      [fullScreen, isDesktop],
    );

    return (
      <Dialog
        ref={ref}
        fullScreen={fullScreen ?? !isDesktop}
        open={open}
        onClose={onClose}
        TransitionComponent={DirectionalSlide}
        PaperProps={{ sx: modalStyles }}
        scroll="paper"
        container={isDesktop ? container : undefined}
        aria-labelledby={modalTitleId}
        disableEscapeKeyDown={isAlertOpen}
        {...restProps}
      >
        {showHeader && (
          <ModalHeader
            showBackButton={showBackButton}
            showCloseButton={showCloseButton}
            onClickBack={onClickBack || onClose}
            onClickClose={onClose}
            modalTitle={modalTitle}
            headerStyles={headerStyles}
            modalTitleProps={{ id: modalTitleId, ...modalTitleProps }}
            showFavorite={showFavorite}
            isFavorited={isFavorited}
            onToggleFavorite={onToggleFavorite}
          />
        )}
        {children}
      </Dialog>
    );
  },
);

Modal.displayName = 'Modal';
